import React, { useState, useEffect } from "react";
import "./NewPatientRegistration.css";

const DoctorRoster = ({ screenAction, triggerSave }) => {
  const [isDisabled, setIsDisabled] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [doctorsList, setDoctorsList] = useState([]); // List of all active doctors
  const [searchedDocs, setSearchedDocs] = useState([]); // Search results
  const [showDropdown, setShowDropdown] = useState(false);
  
  // The table of assigned weekly slots
  const [rosterList, setRosterList] = useState([]);

  const initialFormState = {
    doc_cd: "",
    doctor_name: "",
    department: "",
    
    // Temporary inputs for adding to the list
    current_day: "",
    current_start: "",
    current_end: "",
    current_session: ""
  };

  const [formData, setFormData] = useState(initialFormState);

  // Helper to convert 24h time to 12h AM/PM for display
  const formatTime12h = (time24) => {
    if (!time24) return "";
    let [hour, min] = time24.split(":");
    hour = parseInt(hour, 10);
    const suffix = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour}:${min} ${suffix}`;
  };

  // --- INITIAL DATA LOAD (Fetch all Doctors) ---
  useEffect(() => {
    const fetchAllDoctors = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/doctors?employee_id=${user?.id}`);
        const data = await res.json();
        setDoctorsList(data); 
      } catch (err) {
        console.error("Failed to load doctors", err);
      }
    };
    fetchAllDoctors();
  }, []);

  // --- 1. SEARCH LOGIC (For Edit/View Modes) ---
  const searchRoster = async (text) => {
    setSearchText(text);
    if (text.length < 2) {
      setSearchedDocs([]);
      setShowDropdown(false);
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/doctor-roster/search?q=${text}&employee_id=${user?.id}`);
      const data = await res.json();
      setSearchedDocs(data);
      setShowDropdown(true);
    } catch (err) {
      console.error("Search error:", err);
    }
  };

  // Select a doctor to view/edit their existing schedule
  const selectDoctorToEdit = async (doc) => {
    setFormData(prev => ({
      ...prev,
      doc_cd: doc.doc_cd,
      doctor_name: doc.doctor_name,
      department: doc.department
    }));
    setSearchText(`${doc.doctor_name} | ${doc.doc_cd}`);
    setShowDropdown(false);

    // Fetch their existing schedule
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/doctor-roster/${doc.doc_cd}?employee_id=${user?.id}`);
      const existingRoster = await res.json();
      setRosterList(existingRoster);
    } catch (err) {
      console.error("Error fetching existing roster");
    }

    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
  };

  // Select a doctor when creating a NEW roster
  const handleDoctorSelect = (e) => {
    const selectedCd = e.target.value;
    const docObj = doctorsList.find(d => d.doc_cd === selectedCd);
    
    if (docObj) {
      setFormData(prev => ({
        ...prev,
        doc_cd: docObj.doc_cd,
        doctor_name: docObj.doctor_name,
        department: docObj.department
      }));
      setRosterList([]); // Clear table for new doctor
    } else {
      setFormData(initialFormState);
      setRosterList([]);
    }
  };

  // --- 2. LIST MANAGEMENT ---
  const addSlotToList = () => {
    if (!formData.doc_cd) return alert("Please select a Doctor first.");
    if (!formData.current_day || !formData.current_start || !formData.current_end || !formData.current_session) {
      return alert("Please fill out Day, Start Time, End Time, and Session Type.");
    }
    
    if (formData.current_start >= formData.current_end) {
      return alert("End Time must be after Start Time.");
    }

    const newSlot = {
      day_of_week: formData.current_day,
      start_time: formData.current_start,
      end_time: formData.current_end,
      session_type: formData.current_session,
      isNew: true
    };

    setRosterList([...rosterList, newSlot]);

    // Keep the day selected but clear the times for rapid entry
    setFormData(prev => ({ 
      ...prev, 
      current_start: "", 
      current_end: "",
      current_session: "" 
    }));
  };

  const removeSlot = async (indexToRemove, slotData) => {
    if (slotData.roster_id) {
      const confirmDelete = window.confirm("Remove this time slot from the database?");
      if (!confirmDelete) return;

      try {
        await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/doctor-roster/${slotData.roster_id}`, { method: 'DELETE' });
      } catch (err) {
        console.error("Error deleting from DB");
      }
    }
    setRosterList(rosterList.filter((_, i) => i !== indexToRemove));
  };


  // --- 3. FORM ACTIONS ---
  const clearForm = () => {
    setFormData(initialFormState);
    setRosterList([]);
    setSearchText("");
    setIsDisabled(false);
  };

  useEffect(() => {
    if (triggerSave > 0 && screenAction === "save") handleSubmit();
  }, [triggerSave]);

  useEffect(() => {
    if (screenAction === "add") clearForm();
    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
    if (screenAction === "refresh") clearForm();
    if (screenAction === "print") printForm();
  }, [screenAction]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // --- 4. SAVE LOGIC ---
//   const handleSubmit = async () => {
//     const user = JSON.parse(localStorage.getItem("user"));
//     if (!formData.doc_cd) return alert("Doctor Selection is required!");
//     if (rosterList.length === 0) return alert("Please add at least one schedule slot.");

//     // Filter out slots that are already saved
//     const newSlotsToSave = rosterList.filter(r => r.isNew);

//     if (newSlotsToSave.length === 0) {
//       return alert("No new slots to save.");
//     }

//     const payload = {
//       doc_cd: formData.doc_cd,
//       created_by: user?.id,
//       roster: newSlotsToSave
//     };

//     try {
//       const res = await fetch("https://hmsapi.emedhatechnologies.com/master/api/api/doctor-roster", {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(payload)
//       });
      
//       if (res.ok) {
//         alert("Doctor Schedule Saved Successfully!");
//         clearForm();
//       } else {
//         alert("Error saving schedule");
//       }
//     } catch (err) {
//       alert("Network error while saving.");
//     }
//   };



// --- 4. SAVE LOGIC (Handles POST and PUT) ---
  const handleSubmit = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!formData.doc_cd) return alert("Doctor Selection is required!");
    if (rosterList.length === 0) return alert("Please add at least one schedule slot.");

    // Check if we have any existing records (meaning this is an update)
    // Or if the screen action was explicitly set to 'edit'
    const isExisting = rosterList.some(r => !r.isNew) || screenAction === "edit";

    // Determine Method and URL
    const method = isExisting ? "PUT" : "POST";
    const url = method === "POST" 
      ? "https://hmsapi.emedhatechnologies.com/master/api/api/doctor-roster" 
      : `https://hmsapi.emedhatechnologies.com/master/api/api/doctor-roster/${formData.doc_cd}`;

    // Filter slots: 
    // If it's a POST (new), save only new slots.
    // If it's a PUT (update), send all slots so the backend can ON DUPLICATE KEY UPDATE.
    const slotsToSave = isExisting ? rosterList : rosterList.filter(r => r.isNew);

    if (slotsToSave.length === 0) {
      return alert("No new slots to save.");
    }

    const payload = {
      doc_cd: formData.doc_cd,
      created_by: user?.id,
      updated_by: user?.id, // Send both so backend can use either depending on POST/PUT
      roster: slotsToSave
    };

    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();

      if (res.ok && data.success !== false) {
        alert(`Doctor Schedule ${method === "POST" ? "Saved" : "Updated"} Successfully!`);
        clearForm();
      } else {
        alert("Error: " + (data.message || "Failed to save schedule"));
      }
    } catch (err) {
      console.error("Save error:", err);
      alert("Network error while saving.");
    }
  };

// --- 5. PRINT FORM ---
  const printForm = () => {
    if (!formData.doc_cd || rosterList.length === 0) {
      return alert("No schedule data to print. Please select a doctor and add slots.");
    }

    const newWindow = window.open("", "", "width=900,height=700");
    const today = new Date().toLocaleDateString();

    const htmlContent = `
      <html>
        <head>
          <title>Doctor Schedule - ${formData.doctor_name}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #333; margin: 0; padding: 40px; }
            
            /* Header Section */
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #282f81; padding-bottom: 10px; margin-bottom: 20px; }
            .hospital-info h1 { margin: 0; color: #282f81; font-size: 24px; text-transform: uppercase; }
            .hospital-info p { margin: 2px 0; font-size: 12px; color: #7f8c8d; }
            .report-title { text-align: right; }
            .report-title h2 { margin: 0; color: #11abea; font-size: 18px; }
            
            /* Section Styling */
            .section-header { background: #f2f4f4; padding: 8px 12px; font-weight: bold; font-size: 14px; border-left: 4px solid #282f81; margin-top: 20px; margin-bottom: 10px; text-transform: uppercase; }
            
            /* Data Grid for Doc Info */
            .data-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 10px; }
            .data-item { font-size: 13px; display: flex; border-bottom: 1px dotted #ccc; padding: 4px 0; }
            .label { font-weight: 600; width: 150px; color: #555; }
            .value { flex: 1; color: #000; }
            
            /* Table for Roster */
            table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 13px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
            th { background-color: #f2f4f4; color: #282f81; }

            /* Footer */
            .footer { margin-top: 50px; font-size: 10px; text-align: center; color: #95a5a6; border-top: 1px solid #eee; padding-top: 10px; }
            .sig-box { margin-top: 40px; display: flex; justify-content: space-between; }
            .sig { border-top: 1px solid #000; width: 200px; text-align: center; padding-top: 5px; font-size: 12px; }

            @media print {
              button { display: none; }
              body { padding: 20px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="hospital-info">
              <h1>EMEDHA CARE HOSPITAL</h1>
              <p>123 Medical Park, Health City - 560001</p>
              <p>Phone: +91 98765 43210 | Email: admin@citycare.com</p>
            </div>
            <div class="report-title">
              <h2>DOCTOR SCHEDULE</h2>
              <p>Printed On: ${today}</p>
            </div>
          </div>

          <div class="section-header">Doctor Details</div>
          <div class="data-grid">
            <div class="data-item"><span class="label">Doctor ID:</span> <span class="value">${formData.doc_cd}</span></div>
            <div class="data-item"><span class="label">Name:</span> <span class="value">${formData.doctor_name}</span></div>
            <div class="data-item"><span class="label">Department:</span> <span class="value">${formData.department}</span></div>
          </div>

          <div class="section-header">Standard Weekly Availability</div>
          <table>
            <thead>
              <tr>
                <th>Day of Week</th>
                <th>Time Slot</th>
                <th>Session / Activity Type</th>
              </tr>
            </thead>
            <tbody>
              ${rosterList.map(row => `
                  <tr>
                    <td><strong>${row.day_of_week}</strong></td>
                    <td>${formatTime12h(row.start_time)} to ${formatTime12h(row.end_time)}</td>
                    <td>${row.session_type}</td>
                  </tr>
                `).join('')}
            </tbody>
          </table>

          <div class="sig-box">
            <div class="sig">Doctor Signature</div>
            <div class="sig">Authorized Signatory</div>
          </div>

          <div class="footer">
            This is a computer-generated schedule and is subject to change based on hospital requirements.
          </div>
        </body>
      </html>
    `;

    newWindow.document.write(htmlContent);
    newWindow.document.close();
    
    setTimeout(() => {
      newWindow.print();
      newWindow.close();
    }, 500);
  };

  return (
    <div className="registration-container fade-in">
      
      {/* SEARCH BAR (For Edit/View Modes) */}
      {(screenAction === "edit" || screenAction === "view") && (
        <div className="patient-search">
          <label>Search Existing Schedules</label>
          <input
            type="text"
            value={searchText}
            placeholder="Search Doctor by ID or Name..."
            onChange={(e) => searchRoster(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (searchedDocs.length === 1) selectDoctorToEdit(searchedDocs[0]);
              }
            }}
            autoFocus
          />
          {showDropdown && searchedDocs.length > 0 && (
            <div className="patient-dropdown">
              {searchedDocs.map((doc) => (
                <div key={doc.doc_cd} className="patient-item" onClick={() => selectDoctorToEdit(doc)}>
                  {doc.doctor_name} | {doc.department} | {doc.doc_cd}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <form className="luxury-form">
        
        {/* IDENTIFICATION */}
        <div className="form-section-header">
          <h3>Doctor Selection</h3>
        </div>
        <div className="form-grid">
          
          <div className="input-box">
            <label>Select Doctor <span style={{color:'red'}}>*</span></label>
            <select 
              name="doc_cd" 
              value={formData.doc_cd} 
              onChange={handleDoctorSelect} 
              disabled={isDisabled || screenAction === "edit" || screenAction === "view"}
            >
              <option value="">-- Choose Doctor --</option>
              {doctorsList.map(doc => (
                <option key={doc.doc_cd} value={doc.doc_cd}>
                  {doc.doctor_name} ({doc.department})
                </option>
              ))}
            </select>
          </div>

          <div className="input-box read-only">
            <label>Doctor ID</label>
            <input type="text" value={formData.doc_cd} readOnly placeholder="Auto-fills" />
          </div>
          <div className="input-box read-only">
            <label>Department</label>
            <input type="text" value={formData.department} readOnly placeholder="Auto-fills" />
          </div>
        </div>

        {/* WEEKLY SCHEDULE CONTROLS */}
        <div className="form-section-header" style={{ marginTop: '20px', borderTop: '1px solid #ccc', paddingTop: '10px' }}>
          <h3>Add Standard Weekly Availability</h3>
        </div>
        <div className="form-grid" style={{ alignItems: 'flex-end', backgroundColor: '#eef6fc', padding: '15px', borderRadius: '8px' }}>
          
          <div className="input-box">
            <label>Day of Week</label>
            <select name="current_day" value={formData.current_day} onChange={handleChange} disabled={isDisabled || !formData.doc_cd}>
              <option value="">Select Day</option>
              <option value="Monday">Monday</option>
              <option value="Tuesday">Tuesday</option>
              <option value="Wednesday">Wednesday</option>
              <option value="Thursday">Thursday</option>
              <option value="Friday">Friday</option>
              <option value="Saturday">Saturday</option>
              <option value="Sunday">Sunday</option>
            </select>
          </div>

          <div className="input-box">
            <label>Start Time</label>
            <input type="time" name="current_start" value={formData.current_start} onChange={handleChange} disabled={isDisabled || !formData.doc_cd} />
          </div>
          
          <div className="input-box">
            <label>End Time</label>
            <input type="time" name="current_end" value={formData.current_end} onChange={handleChange} disabled={isDisabled || !formData.doc_cd} />
          </div>

          <div className="input-box">
            <label>Session Type</label>
            <select name="current_session" value={formData.current_session} onChange={handleChange} disabled={isDisabled || !formData.doc_cd}>
              <option value="">Select Type</option>
              <option value="OPD Consultation">OPD Consultation (Walk-ins)</option>
              <option value="Operation Theater">Operation Theater (Surgeries)</option>
              <option value="IPD Rounds">IPD Ward Rounds</option>
              <option value="Video Consultation">Video / Tele-Consultation</option>
              <option value="Admin / Academic">Admin / Academic</option>
            </select>
          </div>
         
        </div>
 <div style={{ display: "flex", justifyContent: "flex-end"}}>
          <button 
             type="button" 
             onClick={addSlotToList} 
             disabled={isDisabled || !formData.current_day || !formData.current_start || !formData.current_end || !formData.current_session}
             style={{ padding: '10px 20px', height: '40px', backgroundColor: '#2980b9', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
             + Add Slot
          </button>
          </div>
        {/* SELECTED SLOTS TABLE */}
        {rosterList.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead style={{ backgroundColor: '#e9ecef', borderBottom: '2px solid #ccc' }}>
                <tr>
                  <th style={{ padding: '10px' }}>Day of Week</th>
                  <th style={{ padding: '10px' }}>Time Slot</th>
                  <th style={{ padding: '10px' }}>Session / Activity Type</th>
                  <th style={{ padding: '10px' }}>Status</th>
                  <th style={{ padding: '10px', textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {rosterList.map((row, index) => {
                  return (
                    <tr key={index} style={{ borderBottom: '1px solid #ddd', backgroundColor: '#fff' }}>
                      <td style={{ padding: '10px', fontWeight: 'bold' }}>{row.day_of_week}</td>
                      <td style={{ padding: '10px' }}>
                        {formatTime12h(row.start_time)} to {formatTime12h(row.end_time)}
                      </td>
                      <td style={{ padding: '10px' }}>{row.session_type}</td>
                      <td style={{ padding: '10px' }}>
                        {row.isNew 
                          ? <span style={{ color: 'orange', fontSize: '12px' }}>Pending Save</span> 
                          : <span style={{ color: 'green', fontSize: '12px' }}>Saved ✓</span>}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        {!isDisabled && (
                           <button type="button" onClick={() => removeSlot(index, row)} style={{ color: 'red', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 'bold' }}>X</button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

      </form>
    </div>
  );
};

export default DoctorRoster;